import DepositCheckConfirmableView from 'features/rdc/screens/ReviewDeposit/DepositCheckConfirmableView';
import DepositCheckErrorView from 'features/rdc/screens/ReviewDeposit/DepositCheckErrorView';
import DepositCheckFixableView from 'features/rdc/screens/ReviewDeposit/DepositCheckFixableView';
import DepositCheckRejectedView from 'features/rdc/screens/ReviewDeposit/DepositCheckRejectedView';
import {
  DepositCheckStatus,
  isSubmitDepositCheckError,
  SubmitDepositCheckError,
} from 'features/rdc/screens/ReviewDeposit/hooks/useSubmitCheckDeposit';
import { TFunction } from 'i18next';
import React from 'react';
import { isCombinedError } from 'shared/utils/errors/combinedErrorUtils';
import { CoreError, isCoreError } from 'shared/utils/errors/CoreError';
import { CombinedError } from 'urql';

interface ErrorOrOutcomeViewProps {
  errorModel: SubmitDepositCheckError | CombinedError | CoreError;
  translatedText: TFunction<'rdc', undefined>;
  defaultError: { title: string; message: string };
  isLoading: boolean;
  handleReviewOrUpdateCheckAction: () => void;
  handleSendCheckAsIsAction: (rdcUploadId?: string) => void;
  handleDoneAction: () => void;
  handleCancelAction: () => void;
  handleRetryAction: () => void;
}

const OutcomeView = ({
  error,
  translatedText,
  isLoading,
  handleReviewOrUpdateCheckAction,
  handleSendCheckAsIsAction,
  handleDoneAction,
  handleCancelAction,
}: {
  error: SubmitDepositCheckError;
  translatedText: TFunction<'rdc', undefined>;
  isLoading: boolean;
  handleReviewOrUpdateCheckAction: () => void;
  handleSendCheckAsIsAction: (rdcUploadId?: string) => void;
  handleDoneAction: () => void;
  handleCancelAction: () => void;
}) => {
  switch (error.depositCheckStatus) {
    case DepositCheckStatus.CONFIRMABLE:
      return (
        <DepositCheckConfirmableView
          errors={error.errors}
          translatedText={translatedText}
          isLoading={isLoading}
          handleReviewCheckAction={handleReviewOrUpdateCheckAction}
          handleConfirmCheckDepositAction={() =>
            handleSendCheckAsIsAction(error.rdcUploadId)
          }
        />
      );
    case DepositCheckStatus.FIXABLE:
      return (
        <DepositCheckFixableView
          errors={error.errors}
          translatedText={translatedText}
          handleUpdateCheckDetailsAction={handleReviewOrUpdateCheckAction}
          handleCancelAction={handleCancelAction}
        />
      );
    case DepositCheckStatus.REJECTED:
      return (
        <DepositCheckRejectedView
          errors={error.errors}
          translatedText={translatedText}
          handleDoneAction={handleDoneAction}
        />
      );
    default:
      return null;
  }
};

const ErrorOrOutcomeView = ({
  errorModel,
  translatedText,
  defaultError,
  isLoading,
  handleReviewOrUpdateCheckAction,
  handleSendCheckAsIsAction,
  handleDoneAction,
  handleCancelAction,
  handleRetryAction,
}: ErrorOrOutcomeViewProps) => {
  if (isSubmitDepositCheckError(errorModel)) {
    return (
      <OutcomeView
        error={errorModel}
        translatedText={translatedText}
        isLoading={isLoading}
        handleReviewOrUpdateCheckAction={handleReviewOrUpdateCheckAction}
        handleSendCheckAsIsAction={handleSendCheckAsIsAction}
        handleDoneAction={handleDoneAction}
        handleCancelAction={handleCancelAction}
      />
    );
  } else if (isCombinedError(errorModel)) {
    const [first] = errorModel.graphQLErrors;
    return (
      <DepositCheckErrorView
        title={defaultError.title}
        message={first.message}
        translatedText={translatedText}
        handleRetryAction={handleRetryAction}
        handleDoneAction={handleDoneAction}
      />
    );
  } else if (isCoreError(errorModel)) {
    return (
      <DepositCheckErrorView
        title={errorModel.title}
        message={errorModel.message}
        translatedText={translatedText}
        handleRetryAction={handleRetryAction}
        handleDoneAction={handleDoneAction}
      />
    );
  }
  return null;
};

export default ErrorOrOutcomeView;
